import type { SkinKind } from "@highmarch/domain";
import { fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { portraitImg } from "../portraits";
import { toast } from "../toast";
import { SkinPreview } from "../../render/skins/skinPreview";
import { WornSkins } from "../../render/skins/wornSkins";
import { emblemArtUrl } from "../../render/skins/emblemArt";
import { HubScene } from "../../render/hubScene";
import { priceHtml } from "./econArt";
import { paintedIconHtml } from "../featureIcons";
import { confirmPurchase, econCue, tickPill } from "./purchaseFx";
import { refusalText } from "./shopActions";
import { SkinAction, SkinCard, actionFor, renderUrl, skinCards, skinTabs, swatchCss } from "./skinsModel";
import { petLink } from "../pets/petLink";
import { petPortraitHtml } from "../pets/petArt";
import { skinsLink } from "./skinsLink";
import { sheetHeadHtml, tagHtml } from "../components";
import { framePreview, killPreview } from "./trophyPreviews";
import { commanderTitle } from "./commanderSheet";
import { openTrophyHall, trophyActionHtml, trophyPriceHtml } from "./trophySkins";
import { notEnoughGems } from "./getGold";

/**
 * The skin picker (FINISH_PLAN WP8): opened from the Towers tab (its Skins button, the Fortress card's brush), the
 * Shop's Skins shelf and the Black Market's skin. One tab per kind — Towers (worn per tower type: pick the tower on
 * the strip), Fortress, Troops, Banner (the pennant's emblem), Road — a live preview of the selected skin (the real
 * model turning, skinPreview.ts; the pennant and the cobbles as pictures), the kind's skins as swatches with what each
 * takes, and one button: WEAR, BUY for gems or Marks, or why it is locked. Looks only: the sheet says so, and nothing
 * here reaches the sim. Hub only (the Shop's rules: a purchase confirms, the pill ticks, a toast says it).
 */
export interface SkinsOpen { readonly kind?: SkinKind; readonly towerId?: string; readonly petId?: string; readonly select?: string | null }

/** The 3D preview's size in CSS px (the stage is as wide as the sheet; the canvas is centred in it). */
const PREVIEW_W = 300, PREVIEW_H = 176;
const COBBLE_URL = "./hq/terrain/cobble_alb.png";

/** The line under the swatches, per tab. */
const FOOT: Readonly<Record<SkinKind, string>> = {
  tower: "Each tower earns a skin at levels 10, 25, 40 and 60. A bought skin fits any tower. Looks only.",
  fortress: "Your fortress wears it in the hub and in every battle. Looks only.",
  troop: "Your troops wear it into battle. Looks only.",
  emblem: "It flies from your fortress's pennant.",
  road: "The road you build in battle is laid in it.",
  frame: "It frames your Commander's name and title: the Commander sheet and your share cards.",
  kill: "Every raider you fell goes out in it. Looks only: off under Reduced effects.",
  pet: "Your pet wears it into battle and round the fortress. Looks only.",
};

/** The Skins art (header, Towers-tab buttons): the painted palette and brush (ui/featureIcons.ts). */
export function skinsIcon(size = 20): string { return paintedIconHtml("skins", size); }

export function openSkins(parent: HTMLElement, o: SkinsOpen = {}): HTMLElement | null {
  const link = skinsLink();
  if (!link) return null;
  const k = link.cosmetics;
  const towers = link.towers();
  let kind: SkinKind = o.kind ?? "tower";
  let towerId = o.towerId && towers.some((t) => t.id === o.towerId) ? o.towerId : towers[0]?.id ?? null;
  let selected: string | null | undefined = o.select;
  // docs/PETS.md: the pet a pet skin goes on (the one asked for, the selected skin's, else the first had)
  // Pets v3: every pet's coats, the pets had first (a pet not caught yet shows how its coats are won)
  const roster = petLink.roster(), pets = [...roster.filter((p) => p.level > 0), ...roster.filter((p) => p.level === 0)];
  const selPet = o.select ? k.skin(o.select)?.pet : undefined;
  let petId = [o.petId, selPet].find((id) => id !== undefined && pets.some((p) => p.id === id)) ?? pets[0]?.id ?? null;
  if (kind === "pet" && petId === null) kind = "tower";
  // M30 #25: what is new on this visit (had, never looked at) wears NEW — on its swatch, its tower and its tab — for
  // as long as the sheet is open. The cosmetics section marks it seen as each tab shows it, so the next visit is calm.
  const fresh = new Set(k.unseen());
  const isFresh = (id: string | null, tower: string | null) => id !== null && (fresh.has(id) || (tower !== null && fresh.has(`${id}@${tower}`)));
  const kindFresh = (kd: SkinKind) => k.list(kd).some((s) => fresh.has(s.id) || [...fresh].some((x) => x.startsWith(`${s.id}@`)));
  const towerFresh = (t: string) => [...fresh].some((x) => x.endsWith(`@${t}`));
  const sheet = document.createElement("div");
  sheet.className = "sheet panel skins towers";
  let preview: SkinPreview | null = null;
  const close = () => { preview?.dispose(); preview = null; sheet.remove(); };

  const petName = () => pets.find((p) => p.id === petId)?.name ?? "pet";
  const towerName = () => (kind === "pet" ? petName() : towers.find((t) => t.id === towerId)?.name ?? "Tower");
  /** The tower (or pet) the tab's skins go on; null for the kinds worn once. */
  const who = () => (kind === "tower" ? towerId : kind === "pet" ? petId : null);
  const render = (): void => {
    const now = link.now();
    const cards = skinCards(k, kind, who(), towerName(), now);
    if (selected === undefined || !cards.some((c) => c.id === selected)) selected = cards.find((c) => c.worn)?.id ?? null;
    const card = cards.find((c) => c.id === selected)!;
    if (k.markSeen(kind, kind === "tower" ? towerId : null)) link.changed();     // what is yours here is no longer new
    const tabs = skinTabs(petLink.owned().length);
    sheet.innerHTML = `
      ${sheetHeadHtml({ title: "Skins", sub: "Looks only: a skin never changes stats", portrait: skinsIcon(46), tint: "plum" })}
      <div class="seg2 sk-tabs t${tabs.length}" role="tablist">${tabs.map((t) => `<button type="button" role="tab" class="sg${t.kind === kind ? " on" : ""}" data-k="${t.kind}" aria-selected="${t.kind === kind}">${t.label}${kindFresh(t.kind) ? `<i class="dot sk-new" aria-label="new"></i>` : ""}</button>`).join("")}</div>
      ${kind === "tower" ? `<div class="sk-towers">${towers.map((t) => `<button class="skt${t.id === towerId ? " on" : ""}" data-t="${t.id}" aria-label="${t.name}">${portraitImg(t.id, 38, icon("tower", 24, "#fff8e6"))}<small>${t.name}</small>${towerFresh(t.id) ? `<i class="dot sk-new" aria-label="new"></i>` : ""}</button>`).join("")}</div>` : ""}
      ${kind === "pet" && pets.length > 1 ? `<div class="sk-towers">${pets.map((p) => `<button class="skp${p.id === petId ? " on" : ""}${p.level > 0 ? "" : " wild"}" data-p="${p.id}" aria-label="${p.name}">${petPortraitHtml(p.id, 38)}<small>${p.name}</small></button>`).join("")}</div>` : ""}
      <div class="sk-stage k-${kind}"><div class="sk-view"></div>
        <div class="sk-cap"><b class="disp">${card.look.name}</b><small>${card.look.blurb}</small></div></div>
      <div class="sk-act">${actionHtml(actionFor(card, kind, towerName()))}</div>
      <div class="sk-grid">${cards.map((c) => cardHtml(c, c.id === selected, isFresh(c.id, kind === "tower" ? towerId : null))).join("")}</div>
      <p class="foot">${FOOT[kind]}</p>`;
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = close;
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".sk-tabs .sg")) b.onclick = () => { if (b.dataset.k !== kind) { kind = b.dataset.k as SkinKind; selected = undefined; render(); } };
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".skt")) b.onclick = () => { if (b.dataset.t !== towerId) { towerId = b.dataset.t!; selected = undefined; render(); } };
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".skp")) b.onclick = () => { if (b.dataset.p !== petId) { petId = b.dataset.p!; selected = undefined; render(); } };
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".skc")) b.onclick = () => { selected = b.dataset.id || null; render(); };
    sheet.querySelector<HTMLButtonElement>(".sk-act .go")?.addEventListener("click", () => void act(card));
    mountView(sheet.querySelector<HTMLElement>(".sk-view")!, card);
  };

  const mountView = (host: HTMLElement, card: SkinCard): void => {
    if (kind === "emblem") { preview?.dispose(); preview = null; host.append(pennant(card)); return; }
    if (kind === "road") { preview?.dispose(); preview = null; host.append(cobbles(card)); return; }
    if (kind === "frame" || kind === "kill") { preview?.dispose(); preview = null; host.append(kind === "frame" ? framePreview(card.look, commanderTitle()) : killPreview(card.look)); return; }
    // a pet whose model has not shipped yet: its portrait (and the CSS stand-in under it) in the stage
    if (kind === "pet" && !SkinPreview.canShowPet(petId ?? "", card.id)) { preview?.dispose(); preview = null; host.insertAdjacentHTML("beforeend", `<div class="sk-pet">${petPortraitHtml(petId ?? "", 132, card.id)}</div>`); return; }
    preview ??= turntable(new SkinPreview(PREVIEW_W, PREVIEW_H));
    host.append(preview.canvas);
    // at the tier the hub stands them at (hubScene.ts tierFor)
    const target = kind === "pet" ? { kind: "pet" as const, petId: petId ?? "" }
      : kind === "troop" ? { kind: "troop" as const }
      : kind === "fortress" ? { kind: "fortress" as const, tier: HubScene.tierFor(link.baseLevel()) }
      : { kind: "tower" as const, archId: towerId ?? "archer", tier: HubScene.tierFor(towers.find((t) => t.id === towerId)?.level ?? 1) };
    void preview.show(target, card.id);
  };

  const act = async (card: SkinCard): Promise<void> => {
    const a = actionFor(card, kind, towerName());
    const now = link.now();
    if (a.kind === "wear") {
      // a fortress skin, or a tower skin with its own shipped model (big kits), is another GLB: the hub is rebuilt round it
      const swap = (skin: string | null) => (WornSkins.swapsTowerModel(towerId ?? "", skin) ? WornSkins.towerModelOf(skin) : null);
      const modelChange = kind === "fortress" ? WornSkins.modelOf(card.id) !== WornSkins.fortressModel()
        : kind === "tower" && swap(card.id) !== swap(WornSkins.of("tower", towerId));
      if (modelChange) close();                     // the hub is rebuilt round the new keep: this sheet goes with it
      const r = k.equip(kind, card.id, who());
      if (r !== true) { toast(parent, refusalText(r)); return; }
      econCue("tap");
      link.changed();
      toast(parent, card.id ? `${card.look.name} on ${kind === "tower" || kind === "pet" ? `your ${towerName()}` : kind === "fortress" ? "your fortress" : kind === "troop" ? "your troops" : kind === "emblem" ? "your banner" : kind === "frame" ? "your name plate" : kind === "kill" ? "your kills" : "your road"}` : `${kind === "tower" || kind === "pet" ? towerName() : "Back"} to ${card.look.name}`);
      if (!modelChange) render();
      return;
    }
    if (a.kind === "market") { close(); link.openBlackMarket(); return; }
    if (a.kind === "trophyHall") { if (card.id && openTrophyHall(parent, card.id)) close(); return; }
    if (a.kind !== "buy" || !card.id) return;
    const wallet = k.wallet, have = a.currency === "gems" ? wallet.gems : wallet.marks;
    if (!a.affordable) {
      if (a.currency === "gems") void notEnoughGems(parent, a.price - have, parent.querySelector(":scope > .sheet.skins"));
      else toast(parent, `You need ${fmtCompact(a.price - have)} more Marks: sell spare gear at the Market`);
      return;
    }
    const ok = await confirmPurchase(parent, { title: "SKIN", currency: a.currency, price: a.price,
      body: `<div class="sk-dlg"><span class="skd" style="background:${swatchCss(card.look)}"></span><div><b class="disp">${card.look.name}</b><small>${card.look.blurb}</small></div></div><p>${kind === "tower" ? "Yours for good, on any tower." : "Yours for good."} Looks only, pure glory.</p>` });
    if (!ok) return;
    const r = k.buy(card.id, now, "hub", a.currency);
    if (r !== true) { toast(parent, refusalText(r)); render(); return; }
    econCue("buy");
    k.equip(kind, card.id, who());
    link.changed();
    tickPill(a.currency, have, a.currency === "gems" ? wallet.gems : wallet.marks);
    toast(parent, `${card.look.name} is yours, and worn`);
    render();
  };

  render();
  parent.append(sheet);
  return sheet;
}

function cardHtml(c: SkinCard, sel: boolean, isNew = false): string {
  const a = c.access;
  const tag = a.have ? (c.worn ? `<span class="skw">${icon("star", 11)} Worn</span>` : "")
    : a.by === "gems" || a.by === "marks" ? priceHtml(a.by, fmtCompact(a.price), 13)
    : a.by === "blackMarket" ? `${priceHtml("gems", fmtCompact(a.price), 13)}` : a.by === "trophies" ? trophyPriceHtml(a.price, 13) : `${icon("lock", 11)}`;
  return `<button class="skc${c.worn ? " worn" : ""}${sel ? " sel" : ""}${a.have ? "" : a.by === "gems" || a.by === "marks" ? " forsale" : " lk"}" data-id="${c.id ?? ""}" aria-label="${c.look.name}${isNew ? ", new" : ""}">
    ${isNew ? tagHtml("new") : ""}<span class="skd" style="background:${swatchCss(c.look, c.art)}"></span><b>${c.look.name}</b><small>${tag}${c.status && !(a.have && c.worn) ? `<i>${c.status}</i>` : ""}</small></button>`;
}

function actionHtml(a: SkinAction): string {
  switch (a.kind) {
    case "worn": return `<button class="btn dark go" disabled><span class="disp">WORN</span></button>`;
    case "wear": return `<button class="btn gold go"><span class="disp">WEAR</span></button>`;
    case "buy": return `<button class="btn ${a.affordable ? (a.currency === "gems" ? "gold" : "blue") : "dark short"} go"><span class="disp">BUY</span>${priceHtml(a.currency, fmtCompact(a.price), 16)}</button>`;
    case "market": return `<button class="btn gold go bm"><span class="disp">BLACK MARKET</span>${priceHtml("gems", fmtCompact(a.price), 16)}</button>`;
    case "locked": return `<div class="sk-lock">${icon("lock", 16)}<span>${a.text}</span></div>`;
    case "trophyHall": return trophyActionHtml(a.price);
  }
}

/**
 * The preview's drag, read here (a DOM control, ADR 0004) and handed to the turntable: a horizontal drag spins the
 * model, and the speed it had when the finger lifted carries on as a flick.
 */
function turntable(preview: SkinPreview): SkinPreview {
  const canvas = preview.canvas;
  let last: { x: number; t: number } | null = null;
  canvas.addEventListener("pointerdown", (e) => { last = { x: e.clientX, t: performance.now() }; canvas.setPointerCapture(e.pointerId); preview.grab(); });
  canvas.addEventListener("pointermove", (e) => {
    if (!last) return;
    const now = performance.now();
    preview.spinBy(e.clientX - last.x, (now - last.t) / 1000);
    last = { x: e.clientX, t: now };
  });
  const up = () => { last = null; preview.release(); };
  canvas.addEventListener("pointerup", up);
  canvas.addEventListener("pointercancel", up);
  return preview;
}

/** The Banner tab's picture: the pennant on its pole, the emblem drawn as the fortress will wear it. */
export function pennant(card: SkinCard): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "sk-flag";
  const e = card.look.emblem;
  const cloth = document.createElement("div");
  cloth.className = "cloth";
  cloth.style.backgroundColor = e ? `#${e.cloth.toString(16).padStart(6, "0")}` : "#2e6ef2";
  cloth.innerHTML = `<img decoding="async" src="${e ? emblemArtUrl(e.glyph) : "./interface/brand/emblem_256.png"}" alt="">`
    + (e?.sheen ? `<i class="csheen s-${e.sheen}"></i><i class="cedge s-${e.sheen}"></i>` : "");     // v2: the banner's touch on the cloth (flagCloth.ts), in CSS
  // the emblem's flourish as the fortress wears it (flagEmblem.ts FlagFlourish): embers off the cloth, a crown on the pole
  const motes = e?.flourish === "embers" || e?.flourish === "leaves" || e?.flourish === "forge" || e?.flourish === "sand" || e?.flourish === "stars";
  const flourish = motes ? `<span class="embers f-${e.flourish}">${"<i></i>".repeat(7)}</span>` : e?.flourish === "crown" ? `<i class="crown"></i>` : "";
  wrap.innerHTML = `<i class="pole"></i>${flourish}`;
  wrap.append(cloth);
  return wrap;
}

/**
 * The Road tab's picture: the in-engine render of a run of road in the skin, with its dressing (sun glyphs, inlays,
 * lamps, rune stones) in a night well; a look without a render gets the game's own cobble map in its tint.
 */
export function cobbles(card: SkinCard): HTMLElement {
  const d = document.createElement("div");
  d.className = "sk-road";
  const pic = renderUrl(card.look);
  if (pic) { d.classList.add("pic"); d.innerHTML = `<img decoding="async" src="${pic}" alt="">`; return d; }
  const m = card.look.road ?? [1, 1, 1];
  const toCss = (v: number) => Math.round(Math.min(1, v * 0.86) * 255);  // the stock grade's brightness, no bloom
  d.style.backgroundImage = `url(${COBBLE_URL})`;
  d.style.backgroundColor = `rgb(${toCss(m[0])}, ${toCss(m[1])}, ${toCss(m[2])})`;
  return d;
}
